import { useParams } from "react-router";
import { useQuery } from "convex/react";
import { api } from "@/convex/_generated/api";
import { Helmet } from "react-helmet-async";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";

export default function MediaViewer() {
  const { slug } = useParams();
  const file = useQuery((api as any).files.getFileBySlug, { slug: slug || "" });

  if (file === undefined) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  if (file === null) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <div className="flex-grow flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-4xl font-bold mb-4">404</h1>
            <p className="text-muted-foreground">File not found</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>{file.title || file.name}</title>
        <meta name="description" content={file.description || `View ${file.name}`} />
        {file.keywords && <meta name="keywords" content={file.keywords} />}
        {file.author && <meta name="author" content={file.author} />}
        {file.copyright && <meta name="copyright" content={file.copyright} />}
        
        {/* Open Graph */}
        <meta property="og:title" content={file.title || file.name} />
        <meta property="og:description" content={file.description || `View ${file.name}`} />
        {file.type.startsWith("image/") && <meta property="og:image" content={file.url} />}
        <meta property="og:type" content="website" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={file.title || file.name} />
        <meta name="twitter:description" content={file.description || `View ${file.name}`} />
        {file.type.startsWith("image/") && <meta name="twitter:image" content={file.url} />}
      </Helmet>

      <Navbar />
      
      <main className="flex-grow pt-32 pb-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto w-full">
        <div className="bg-card border rounded-lg overflow-hidden shadow-sm">
          <div className="p-6 border-b">
            <div className="flex justify-between items-start">
              <div>
                <h1 className="text-2xl font-bold mb-2">{file.title || file.name}</h1>
                {file.description && <p className="text-muted-foreground">{file.description}</p>}
              </div>
              <Button asChild variant="outline">
                <a href={file.url} download={file.name} target="_blank" rel="noopener noreferrer">
                  <Download className="h-4 w-4 mr-2" />
                  Download Original
                </a>
              </Button>
            </div>
          </div>
          
          <div className="p-6 flex justify-center bg-muted/20 min-h-[400px] items-center">
            {file.type.startsWith("image/") ? (
              <img
                src={file.url}
                alt={file.alt || file.name}
                className="max-w-full max-h-[80vh] object-contain rounded shadow-sm"
              />
            ) : file.type === "application/pdf" ? (
              <iframe
                src={file.url}
                className="w-full h-[80vh] rounded border"
                title={file.name}
              />
            ) : (
              <div className="text-center p-12">
                <p className="text-lg mb-4">Preview not available for this file type.</p>
                <Button asChild>
                  <a href={file.url} download={file.name}>Download File</a>
                </Button>
              </div>
            )}
          </div>
          
          <div className="p-6 bg-muted/10 text-sm text-muted-foreground grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <span className="font-medium block text-foreground">Type</span>
              {file.type}
            </div>
            <div>
              <span className="font-medium block text-foreground">Size</span>
              {(file.size / 1024).toFixed(2)} KB
            </div>
            <div>
              <span className="font-medium block text-foreground">Uploaded</span>
              {new Date(file._creationTime).toLocaleDateString()}
            </div>
            {file.author && (
              <div>
                <span className="font-medium block text-foreground">Author</span>
                {file.author}
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
